import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { message } from "antd";
import { getZulipChannels, logZulipTopicCreated } from "@/services/app/tenant/integration/zulipChannels";
import {
  addReaction,
  createOrJoinChannel,
  getAllStreams,
  getChannelFolders,
  getMessages,
  getMySubscriptions,
  getRawMessageContent,
  getReadReceipts,
  getRealmPresence,
  getRealmUsers,
  getStreamTopics,
  inviteToChannel,
  markMessagesRead,
  removeReaction,
  sendDirectMessage,
  sendStreamMessage,
  sendTypingStatus,
  setChannelPinned,
  updateOwnPresence,
} from "./chatApi";
import { dmConversationFromMessage, dmKeyFromUserIds, loadDmConversations, upsertDmConversation } from "./useConversations";
import { getMyUserId, messagePermalink } from "./zulipClient";
import { loadZulipEmojiNames } from "./emojiCodeMap";
import { getTenantUsers } from "@/services/app/employee/employee";
import { useZulipEvents, type UnreadState } from "./useZulipEvents";
import type { ChatMode, ChatViewKind, DmConversation, ForwardDestination, PresenceStatus, Selection, ZulipChannelFolder, ZulipEvent, ZulipMessage, ZulipReaction, ZulipStream, ZulipTopic, ZulipUser } from "./types";

// A user is only "active"/"idle" if their presence timestamp is within this
// many seconds of the server's clock — otherwise treated as offline. Mirrors
// the server's own OFFLINE_THRESHOLD_SECS default (zproject/default_settings.py);
// not fetched live since realm/presence doesn't return it.
const PRESENCE_OFFLINE_THRESHOLD_SECS = 200;

const MODE_KEY = "FR_CHAT_MODE";
const OPEN_KEY = "FR_CHAT_OPEN";

function narrowFor(sel: Selection): Array<{ operator: string; operand: unknown }> {
  return sel.kind === "channel"
    ? [
        { operator: "stream", operand: sel.streamId },
        { operator: "topic", operand: sel.topic },
      ]
    : [{ operator: "dm", operand: sel.userIds }];
}

function unreadKeyFor(sel: Selection): { bucket: "byChannelTopic" | "byDmKey"; key: string } {
  return sel.kind === "channel"
    ? { bucket: "byChannelTopic", key: `${sel.streamId}:${sel.topic}` }
    : { bucket: "byDmKey", key: dmKeyFromUserIds(sel.userIds) };
}

function isMention(msg: ZulipMessage): boolean {
  return !!msg.flags?.some((f) => f === "mentioned" || f === "wildcard_mentioned");
}

// Zulip's own quote-and-reply format (web/src/compose_reply.ts):
//   @_**Sender Name** [said](link to message):
//   ```quote
//   raw message content
//   ```
// `@_**...**` is a "silent mention" (no notification, unlike `@**...**`) —
// used here since the point is attribution, not re-notifying the sender.
// Shared by quoteMessage (inserted into the open thread's composer) and
// forwardMessage (sent straight to a different destination) — identical
// content either way, only the send target differs.
function buildQuoteBlock(msg: ZulipMessage, rawContent: string): string {
  const link = messagePermalink(msg);
  const header = link ? `@_**${msg.sender_full_name}** [said](${link}):` : `@_**${msg.sender_full_name}** said:`;
  return `${header}\n\`\`\`quote\n${rawContent}\n\`\`\`\n`;
}

function readMode(): ChatMode {
  try {
    const v = localStorage.getItem(MODE_KEY);
    if (v === "sidebar" || v === "widget") return v;
  } catch {
    // ignore
  }
  return "widget";
}

function readOpen(): boolean {
  try {
    return localStorage.getItem(OPEN_KEY) === "1";
  } catch {
    return false;
  }
}

function writeOpen(v: boolean): void {
  try {
    localStorage.setItem(OPEN_KEY, v ? "1" : "0");
  } catch {
    // ignore
  }
}

type ChatContextValue = {
  ready: boolean;
  myUserId: number | null;

  mode: ChatMode;
  setMode: (mode: ChatMode) => void;
  open: boolean;
  toggleOpen: () => void;
  setOpen: (v: boolean) => void;

  subscriptions: ZulipStream[];
  browseStreams: ZulipStream[];
  channelFolders: ZulipChannelFolder[];
  streamTopics: Record<number, ZulipTopic[]>;
  loadTopicsFor: (streamId: number) => Promise<void>;
  refreshSubscriptions: () => Promise<void>;
  refreshBrowseStreams: () => Promise<void>;
  expandedStreamIds: Set<number>;
  toggleStreamExpanded: (streamId: number) => void;

  dmConversations: DmConversation[];
  refreshDmConversations: () => Promise<void>;

  selection: Selection | null;
  selectChannel: (stream: ZulipStream, topic: string) => void;
  selectDm: (userIds: number[], label: string) => void;
  clearSelection: () => void;

  // Zulip-style sidebar "views" (Inbox, Mentions, Combined feed, Starred
  // messages, Recent conversations) — mutually exclusive with `selection`,
  // since only one of "a single thread" or "a cross-conversation feed" is
  // shown at a time.
  activeView: ChatViewKind | null;
  setActiveView: (view: ChatViewKind | null) => void;
  unread: UnreadState;

  // Read receipts ("Allow other users to view read receipts", an
  // organization-level setting) — realm-wide on/off switch, and a
  // per-message id -> reader-user-ids cache populated on demand by
  // loadReadReceipts (see the poll effect below for what keeps it fresh).
  enableReadReceipts: boolean;
  readReceipts: Record<number, number[]>;
  loadReadReceipts: (messageId: number) => void;

  messages: ZulipMessage[];
  messagesLoading: boolean;
  hasOlder: boolean;
  loadOlderMessages: () => Promise<void>;
  sendMessage: (content: string, topicOverride?: string) => Promise<void>;
  toggleReaction: (messageId: number, reaction: { emojiName: string; emojiCode: string; reactionType: string }) => void;

  // "Quote message" — quoteMessage stages a markdown block for the open
  // thread's Composer (pendingComposerText); the Composer clears it via
  // consumePendingComposerText once inserted.
  pendingComposerText: string | null;
  consumePendingComposerText: () => void;
  quoteMessage: (msg: ZulipMessage) => Promise<void>;
  // "Forward message" — mirrors inviteTarget/setInviteTarget's
  // open-a-modal-via-state pattern (see ForwardMessageModal).
  forwardTarget: ZulipMessage | null;
  setForwardTarget: (msg: ZulipMessage | null) => void;
  forwardMessage: (msg: ZulipMessage, dest: ForwardDestination) => Promise<void>;
  typingUsers: string[];
  typingIntervals: { startMs: number; stopMs: number };
  sendTypingSignal: (op: "start" | "stop") => void;

  unreadTotal: number;
  unreadForChannelTopic: (streamId: number, topic: string) => number;
  unreadForDm: (userIds: number[]) => number;
  unreadForStream: (streamId: number) => number;

  realmUsers: ZulipUser[];
  refreshRealmUsers: () => Promise<void>;
  presenceStatusFor: (email: string) => PresenceStatus;
  roleFor: (email: string) => string | undefined;
  userTypeFor: (email: string) => string | undefined;

  createChannel: (opts: { name: string; description?: string; inviteOnly: boolean; principals: number[] }) => Promise<ZulipStream | null>;
  invite: (streamName: string, principals: number[]) => Promise<void>;
  togglePinned: (stream: ZulipStream) => Promise<void>;

  newChannelOpen: boolean;
  setNewChannelOpen: (v: boolean) => void;
  inviteTarget: ZulipStream | null;
  setInviteTarget: (s: ZulipStream | null) => void;
  newDmOpen: boolean;
  setNewDmOpen: (v: boolean) => void;

  // Pushed down from ChatWidget (see the comment on ChatProvider's own
  // canManageChatChannels state) so refreshBrowseStreams can fold in every
  // channel that exists, not just the ones the current user's own Zulip
  // credentials can see.
  setCanManageChatChannels: (v: boolean) => void;
};

const ChatContext = createContext<ChatContextValue | null>(null);

export function useChat(): ChatContextValue {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error("useChat() must be used within <ChatProvider>");
  return ctx;
}

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [ready, setReady] = useState(false);
  const [myUserId, setMyUserId] = useState<number | null>(null);
  // ChatProvider is mounted in app.tsx's rootContainer, above every UmiJS
  // runtime provider (including the access plugin's) — see the comment
  // there. useAccess() would resolve to the AccessContext's default (null)
  // if called from here, so this flows in the other direction: ChatWidget,
  // which IS rendered underneath the access provider, pushes its own
  // useAccess() result down via setCanManageChatChannels.
  const [canManageChatChannels, setCanManageChatChannels] = useState(false);

  const [mode, setModeState] = useState<ChatMode>(readMode);
  // Persisted (not just in-memory) so a pinned/open sidebar survives a page
  // reload instead of collapsing back to the rail every time it's visited.
  const [open, setOpenState] = useState<boolean>(readOpen);

  const [subscriptions, setSubscriptions] = useState<ZulipStream[]>([]);
  const [browseStreams, setBrowseStreams] = useState<ZulipStream[]>([]);
  const [channelFolders, setChannelFolders] = useState<ZulipChannelFolder[]>([]);
  const [streamTopics, setStreamTopics] = useState<Record<number, ZulipTopic[]>>({});
  // Lives here (not as ConversationList's own local state) because in widget
  // mode ConversationList unmounts whenever a topic/DM is selected (single-pane
  // navigation swaps it for ThreadView) — local state would reset to collapsed
  // every time the user came back from a thread.
  const [expandedStreamIds, setExpandedStreamIds] = useState<Set<number>>(new Set());

  const [dmConversations, setDmConversations] = useState<DmConversation[]>([]);
  const [realmUsers, setRealmUsers] = useState<ZulipUser[]>([]);
  const [presenceByEmail, setPresenceByEmail] = useState<Record<string, { status: "active" | "idle"; timestamp: number }>>({});
  const [presenceServerTimestamp, setPresenceServerTimestamp] = useState(0);
  // Butler's own user-type/role per email (Zulip has no concept of either) —
  // keyed lowercase since email casing isn't guaranteed to match between
  // Zulip's account records and Butler's own. userType is the raw 'tenant' |
  // 'employee' value (for grouping people-pickers by it); role is the
  // specific role name within that type (e.g. "CSR", "Technician"), when one
  // is assigned.
  const [userMetaByEmail, setUserMetaByEmail] = useState<Record<string, { userType?: string; role?: string }>>({});

  const [selection, setSelection] = useState<Selection | null>(null);
  const [activeView, setActiveViewState] = useState<ChatViewKind | null>(null);
  const [messages, setMessages] = useState<ZulipMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [hasOlder, setHasOlder] = useState(false);
  const selectionRef = useRef<Selection | null>(null);
  selectionRef.current = selection;
  const messagesRef = useRef<ZulipMessage[]>([]);
  messagesRef.current = messages;
  const streamTopicsRef = useRef<Record<number, ZulipTopic[]>>({});
  streamTopicsRef.current = streamTopics;

  const [newChannelOpen, setNewChannelOpen] = useState(false);
  const [inviteTarget, setInviteTarget] = useState<ZulipStream | null>(null);
  const [newDmOpen, setNewDmOpen] = useState(false);

  // "Quote message" stages a markdown block here for the currently-mounted
  // Composer to pick up (see Composer's own effect) — quoting only ever
  // targets the thread the quoted message itself belongs to, so there's no
  // ambiguity about which Composer instance should consume it.
  const [pendingComposerText, setPendingComposerText] = useState<string | null>(null);
  // "Forward message" target — mirrors inviteTarget's open-a-modal-via-state
  // pattern (see ForwardMessageModal), non-null while that modal is open.
  const [forwardTarget, setForwardTarget] = useState<ZulipMessage | null>(null);

  // handleIncomingMessage (defined further down, after the setup it needs)
  // closes over state set up later in this component — this ref lets it be
  // registered with useZulipEvents right away via a permanently-stable
  // forwarding function, rather than needing useZulipEvents itself declared
  // after everything it would otherwise depend on.
  const messageHandlerRef = useRef<(msg: ZulipMessage) => void>(() => {});
  const stableOnMessage = useCallback((msg: ZulipMessage) => messageHandlerRef.current(msg), []);
  const typingHandlerRef = useRef<(ev: ZulipEvent) => void>(() => {});
  const stableOnTyping = useCallback((ev: ZulipEvent) => typingHandlerRef.current(ev), []);

  // Patches in the real thumbnail HTML once Zulip's async worker replaces a
  // freshly uploaded image's loading-spinner placeholder (or a genuine
  // content edit lands) — see useZulipEvents' "update_message" handling.
  const handleMessageUpdate = useCallback((messageIds: number[], renderedContent: string) => {
    setMessages((prev) => prev.map((m) => (messageIds.includes(m.id) ? { ...m, content: renderedContent } : m)));
  }, []);

  // Adds/removes one reaction in a message's `reactions` array — shared by
  // toggleReaction's optimistic local update and handleReactionEvent's
  // application of the server's own echo of it, so both converge on the
  // same shape (and the echo becomes a harmless no-op via the dedupe below).
  const patchReaction = useCallback((messageId: number, op: "add" | "remove", reaction: ZulipReaction) => {
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== messageId) return m;
        const existing = m.reactions ?? [];
        const sameReaction = (r: ZulipReaction) =>
          r.user_id === reaction.user_id && r.emoji_code === reaction.emoji_code && r.reaction_type === reaction.reaction_type;
        if (op === "add") {
          return existing.some(sameReaction) ? m : { ...m, reactions: [...existing, reaction] };
        }
        return { ...m, reactions: existing.filter((r) => !sameReaction(r)) };
      }),
    );
  }, []);

  // A "reaction" event (add or remove, from any user including ourselves —
  // see useZulipEvents' onReaction) landed on the queue; patch it in.
  const handleReactionEvent = useCallback((ev: ZulipEvent) => {
    if (ev.message_id === undefined || ev.user_id === undefined || !ev.emoji_code || !ev.emoji_name || !ev.reaction_type) return;
    if (ev.op !== "add" && ev.op !== "remove") return;
    patchReaction(ev.message_id, ev.op, {
      emoji_name: ev.emoji_name,
      emoji_code: ev.emoji_code,
      reaction_type: ev.reaction_type as ZulipReaction["reaction_type"],
      user_id: ev.user_id,
    });
  }, [patchReaction]);

  // Adds the caller's reaction if they haven't already reacted with this
  // exact emoji on this message, otherwise removes it (Slack/Discord-style
  // toggle) — used by both the reaction-pill click and the emoji picker.
  // Optimistic: patches local state immediately rather than waiting on
  // Zulip's own echo of the "reaction" event, since the picker's Popover
  // renders in a portal outside the message row — MessageBubble's hover
  // state has already gone false by the time the click handler here runs,
  // so without an instant local update the just-added pill would flicker
  // out and back in while the event round-trips. Reverts + surfaces the
  // error if the call actually fails; the real event's own arrival later is
  // a harmless no-op via patchReaction's dedupe.
  const toggleReaction = useCallback((messageId: number, reaction: { emojiName: string; emojiCode: string; reactionType: string }) => {
    if (myUserId === null) return;
    // emoji-picker-react's own name for a glyph (e.g. "thumbs up") essentially
    // never matches Zulip's internal spelling (e.g. "+1"), and Zulip's
    // check_emoji_request rejects a unicode_emoji reaction whose emoji_name
    // doesn't map back to the exact emoji_code sent — swap in Zulip's own
    // canonical name (loaded into zulipEmojiNamesRef above) when we have it,
    // falling back to the picker's name only if that lookup hasn't loaded yet.
    const emojiName =
      reaction.reactionType === "unicode_emoji"
        ? zulipEmojiNamesRef.current.get(reaction.emojiCode) ?? reaction.emojiName
        : reaction.emojiName;
    const full: ZulipReaction = {
      emoji_name: emojiName,
      emoji_code: reaction.emojiCode,
      reaction_type: reaction.reactionType as ZulipReaction["reaction_type"],
      user_id: myUserId,
    };
    const msg = messagesRef.current.find((m) => m.id === messageId);
    const alreadyMine = msg?.reactions?.some(
      (r) => r.user_id === myUserId && r.emoji_code === reaction.emojiCode && r.reaction_type === reaction.reactionType,
    );
    patchReaction(messageId, alreadyMine ? "remove" : "add", full);
    const call = alreadyMine ? removeReaction : addReaction;
    call(messageId, emojiName, reaction.emojiCode, reaction.reactionType).catch((e: any) => {
      patchReaction(messageId, alreadyMine ? "add" : "remove", full);
      message.error(e?.message || "Failed to update reaction.");
    });
  }, [myUserId, patchReaction]);

  // Who's currently typing in the active thread — sender user ids, each with
  // a safety-net auto-clear timeout in case a "stop" event is ever missed.
  const [typingSenderIds, setTypingSenderIds] = useState<Set<number>>(new Set());
  const typingTimeoutsRef = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  const setMode = useCallback((next: ChatMode) => {
    setModeState(next);
    try {
      localStorage.setItem(MODE_KEY, next);
    } catch {
      // ignore
    }
  }, []);

  const setOpen = useCallback((v: boolean) => {
    setOpenState(v);
    writeOpen(v);
  }, []);

  const toggleOpen = useCallback(() => {
    setOpenState((prev) => {
      const next = !prev;
      writeOpen(next);
      return next;
    });
  }, []);

  // ---- bootstrap: resolve our own Zulip identity once per session ----
  useEffect(() => {
    let cancelled = false;
    getMyUserId()
      .then((id) => {
        if (!cancelled) {
          setMyUserId(id);
          setReady(true);
        }
      })
      .catch(() => {
        // Chat isn't available (not configured / unreachable) — fail quiet,
        // the bell/panel just show an empty state rather than crashing the layout.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const refreshSubscriptions = useCallback(async () => {
    try {
      setSubscriptions(await getMySubscriptions());
    } catch {
      // leave previous list in place
    }
  }, []);

  // Zulip's real long-poll (register + GET events) — see useZulipEvents.ts.
  // `stableOnMessage` forwards to `handleIncomingMessage`, defined further
  // down once loadTopicsFor/markMessagesRead etc. are in scope.
  // `refreshSubscriptions` is itself a stable (no-dep) callback so it's safe
  // to pass directly.
  const { unread, bumpUnread, bumpMentions, typingIntervals, enableReadReceipts, emojiDataUrl } = useZulipEvents(myUserId, ready, {
    onMessage: stableOnMessage,
    onSubscriptionChange: refreshSubscriptions,
    onTyping: stableOnTyping,
    onMessageUpdate: handleMessageUpdate,
    onReaction: handleReactionEvent,
  });

  // Zulip's own canonical unicode-emoji names, keyed by hex codepoint — see
  // emojiCodeMap.ts and toggleReaction below for why this is needed at all.
  // A ref (not state) since it's read inside a callback but should never by
  // itself trigger a re-render.
  const zulipEmojiNamesRef = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    if (!emojiDataUrl) return;
    let cancelled = false;
    loadZulipEmojiNames(emojiDataUrl)
      .then((map) => {
        if (!cancelled) zulipEmojiNamesRef.current = map;
      })
      .catch(() => {
        // Reactions still work without it — toggleReaction falls back to
        // the picker's own name, it just risks "Invalid emoji name".
      });
    return () => {
      cancelled = true;
    };
  }, [emojiDataUrl]);

  // "Manage Chat Channels" holders get discovery visibility into every
  // channel that exists (including private ones they haven't joined) — not
  // read/post access, just the ability to see it in the Browse list, sourced
  // from the admin bot-credential endpoint (ZulipService::listChannels,
  // include_all_active=true) rather than their own personal Zulip API key,
  // which per Zulip's own access rules can never see an unsubscribed private
  // stream. Skipped entirely for everyone else so their client never calls an
  // endpoint they aren't permitted to use.
  const refreshBrowseStreams = useCallback(async () => {
    try {
      const [memberVisible, allChannels] = await Promise.all([
        getAllStreams(),
        canManageChatChannels ? getZulipChannels().catch(() => []) : Promise.resolve([]),
      ]);
      const byId = new Map<number, ZulipStream>();
      memberVisible.forEach((s) => byId.set(s.stream_id, s));
      allChannels.forEach((c) => {
        if (!byId.has(c.stream_id)) byId.set(c.stream_id, c);
      });
      setBrowseStreams([...byId.values()]);
    } catch {
      // ignore
    }
  }, [canManageChatChannels]);

  const refreshChannelFolders = useCallback(async () => {
    try {
      setChannelFolders(await getChannelFolders());
    } catch {
      // Older servers without channel folders — the grouping helper just
      // falls back to a single flat "Channels" section.
    }
  }, []);

  const refreshDmConversations = useCallback(async () => {
    if (!myUserId) return;
    try {
      setDmConversations(await loadDmConversations(myUserId));
    } catch {
      // ignore
    }
  }, [myUserId]);

  const refreshRealmUsers = useCallback(async () => {
    try {
      setRealmUsers(await getRealmUsers());
    } catch {
      // ignore
    }
  }, []);

  const refreshPresence = useCallback(async () => {
    try {
      const res = await getRealmPresence();
      const byEmail: Record<string, { status: "active" | "idle"; timestamp: number }> = {};
      Object.entries(res.presences ?? {}).forEach(([email, info]) => {
        if (info?.aggregated) byEmail[email] = { status: info.aggregated.status, timestamp: info.aggregated.timestamp };
      });
      setPresenceByEmail(byEmail);
      setPresenceServerTimestamp(res.server_timestamp ?? Date.now() / 1000);
    } catch {
      // ignore
    }
  }, []);

  // Resolves each Butler user's role/type label (e.g. "Technician", "Super
  // Admin", "CSR") from Butler's own backend — separate from Zulip's
  // realm-user list, keyed by email to cross-reference the two. Fetched
  // once; role assignments don't change often enough within a session to
  // warrant polling.
  const refreshRoles = useCallback(async () => {
    try {
      const res = await getTenantUsers({ with: "tenantUserRoles,employeeRoles" });
      const users = Array.isArray(res) ? res : (res as any)?.data ?? [];
      const byEmail: Record<string, { userType?: string; role?: string }> = {};
      for (const u of users as API.CurrentUser[]) {
        const email = u.user_email?.toLowerCase();
        if (!email) continue;
        const roles = u.user_type === "employee" ? u.employee_roles : u.tenant_user_roles;
        const first = Array.isArray(roles) ? roles[0] : undefined;
        const roleName = typeof first === "string" ? first : (first as any)?.role;
        byEmail[email] = { userType: u.user_type, role: roleName || undefined };
      }
      setUserMetaByEmail(byEmail);
    } catch {
      // ignore — the people list just shows no role label
    }
  }, []);

  // Specific role name (e.g. "CSR"), falling back to the generic user-type
  // label ("Tenant" / "Employee") when no specific role is assigned.
  const roleFor = useCallback((email: string) => {
    const meta = userMetaByEmail[email.toLowerCase()];
    if (!meta) return undefined;
    if (meta.role) return meta.role;
    if (meta.userType === "tenant") return "Tenant";
    if (meta.userType === "employee") return "Employee";
    return undefined;
  }, [userMetaByEmail]);

  // Raw 'tenant' | 'employee' value, for grouping people-pickers by user type.
  const userTypeFor = useCallback((email: string) => userMetaByEmail[email.toLowerCase()]?.userType, [userMetaByEmail]);

  useEffect(() => {
    if (!ready) return;
    refreshSubscriptions();
    refreshDmConversations();
    refreshChannelFolders();
    // Fetched up front (not just lazily on "Browse channels") so the
    // persistent "Browse N more channels" link has a count to show right away.
    refreshBrowseStreams();
    // Needed up front (not just lazily for the invite/new-DM pickers) so
    // typing indicators can resolve a sender_id to a display name.
    refreshRealmUsers();
    refreshRoles();
  }, [ready, refreshSubscriptions, refreshDmConversations, refreshChannelFolders, refreshBrowseStreams, refreshRealmUsers, refreshRoles]);

  // Reports *our own* presence for as long as the app is loaded — deliberately
  // NOT gated on `open`. ChatProvider is mounted at the app shell (app.tsx),
  // so `ready` alone tracks "the user is using the app"; gating this on the
  // chat panel being open would mean a user only counts as online to everyone
  // else while they personally have the panel open, going stale ~200s after
  // they close it even though they're still active elsewhere. Zulip only has
  // fresh data to hand back for a user once some client pings on their
  // behalf, so without this every user in the people list would read as
  // permanently offline.
  useEffect(() => {
    if (!ready) return;
    const ping = () => void updateOwnPresence(document.visibilityState === "hidden" ? "idle" : "active");
    ping();
    const id = setInterval(ping, 60000);
    return () => clearInterval(id);
  }, [ready]);

  // Presence is a plain periodic *read* poll (not part of the event queue)
  // and only while the panel is actually open — it exists solely to drive
  // the people list's status dots, so there's no point paying for it while
  // collapsed.
  useEffect(() => {
    if (!ready || !open) return;
    refreshPresence();
    const id = setInterval(refreshPresence, 60000);
    return () => clearInterval(id);
  }, [ready, open, refreshPresence]);

  // Read receipts cache — keyed by message id, since Zulip's read-receipts
  // endpoint (GET messages/{id}/read_receipts) is per-message with no bulk
  // equivalent (see chatApi.getReadReceipts). readReceiptsInFlightRef
  // dedupes concurrent calls for the same message id (the poll effect below
  // and a component's own mount can both ask for the same id).
  const [readReceipts, setReadReceipts] = useState<Record<number, number[]>>({});
  const readReceiptsInFlightRef = useRef<Set<number>>(new Set());

  const loadReadReceipts = useCallback((messageId: number) => {
    if (readReceiptsInFlightRef.current.has(messageId)) return;
    readReceiptsInFlightRef.current.add(messageId);
    getReadReceipts(messageId)
      .then((userIds) => setReadReceipts((prev) => ({ ...prev, [messageId]: userIds })))
      .catch(() => {
        // Disabled org-wide, or a transient error — leave this id uncached;
        // callers treat "no entry" as "unknown yet", not "unread".
      })
      .finally(() => {
        readReceiptsInFlightRef.current.delete(messageId);
      });
  }, []);

  // Zulip pushes no event when someone else reads *your* message (its own
  // read-receipts modal polls for the same reason — see read_receipts.ts in
  // the Zulip webapp), so this refreshes on an interval instead: the last
  // message of every DM conversation I sent (drives ConversationList's
  // per-row indicator), plus the open thread's last message if it's mine
  // (drives ThreadView's). Gated on the panel being open and the realm
  // feature being on, same as the presence poll above.
  useEffect(() => {
    if (!ready || !open || !enableReadReceipts || myUserId === null) return;
    const tick = () => {
      const ids = new Set<number>();
      dmConversations.forEach((c) => {
        if (c.lastMessage && c.lastMessage.sender_id === myUserId) ids.add(c.lastMessage.id);
      });
      if (messages.length) {
        const last = messages[messages.length - 1];
        if (last.sender_id === myUserId) ids.add(last.id);
      }
      ids.forEach(loadReadReceipts);
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [ready, open, enableReadReceipts, myUserId, dmConversations, messages, loadReadReceipts]);

  const presenceStatusFor = useCallback((email: string): PresenceStatus => {
    const info = presenceByEmail[email];
    if (!info) return "offline";
    if (presenceServerTimestamp - info.timestamp >= PRESENCE_OFFLINE_THRESHOLD_SECS) return "offline";
    return info.status;
  }, [presenceByEmail, presenceServerTimestamp]);

  const loadTopicsFor = useCallback(async (streamId: number) => {
    try {
      const topics = await getStreamTopics(streamId);
      setStreamTopics((prev) => ({ ...prev, [streamId]: topics }));
    } catch {
      // ignore
    }
  }, []);

  const toggleStreamExpanded = useCallback((streamId: number) => {
    setExpandedStreamIds((prev) => {
      const next = new Set(prev);
      if (next.has(streamId)) {
        next.delete(streamId);
      } else {
        next.add(streamId);
        if (!streamTopicsRef.current[streamId]) loadTopicsFor(streamId);
      }
      return next;
    });
  }, [loadTopicsFor]);

  // ---- selection & message thread ----
  const loadThread = useCallback(async (sel: Selection) => {
    setMessagesLoading(true);
    try {
      const res = await getMessages({ narrow: narrowFor(sel), anchor: "newest", numBefore: 50 });
      if (selectionRef.current === sel || JSON.stringify(selectionRef.current) === JSON.stringify(sel)) {
        setMessages(res.messages);
        setHasOlder(res.found_oldest === false);
        const unreadMessages = res.messages.filter((m) => !m.flags?.includes("read"));
        if (unreadMessages.length) {
          const { bucket, key } = unreadKeyFor(sel);
          const mentionCount = unreadMessages.filter(isMention).length;
          markMessagesRead(unreadMessages.map((m) => m.id))
            .then(() => {
              bumpUnread(bucket, key, -unreadMessages.length);
              if (mentionCount) bumpMentions(-mentionCount);
            })
            .catch(() => {});
        }
      }
    } catch {
      setMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  }, [bumpUnread, bumpMentions]);

  const selectChannel = useCallback((stream: ZulipStream, topic: string) => {
    const sel: Selection = { kind: "channel", streamId: stream.stream_id, streamName: stream.name, topic };
    setActiveViewState(null);
    setSelection(sel);
    loadThread(sel);
  }, [loadThread]);

  const selectDm = useCallback((userIds: number[], label: string) => {
    const sel: Selection = { kind: "dm", userIds, label };
    setActiveViewState(null);
    setSelection(sel);
    loadThread(sel);
  }, [loadThread]);

  const clearSelection = useCallback(() => {
    setSelection(null);
    setMessages([]);
  }, []);

  // Setting a view clears the current thread selection — the main pane shows
  // either a single thread or a cross-conversation feed, never both.
  const setActiveView = useCallback((view: ChatViewKind | null) => {
    setActiveViewState(view);
    if (view) {
      setSelection(null);
      setMessages([]);
    }
  }, []);

  const loadOlderMessages = useCallback(async () => {
    const sel = selectionRef.current;
    if (!sel || !messages.length) return;
    const oldestId = messages[0]?.id;
    if (!oldestId) return;
    try {
      const res = await getMessages({ narrow: narrowFor(sel), anchor: oldestId, numBefore: 50, numAfter: 0 });
      const older = res.messages.filter((m) => m.id !== oldestId);
      setMessages((prev) => [...older, ...prev]);
      setHasOlder(res.found_oldest === false);
    } catch {
      // ignore
    }
  }, [messages]);

  const sendMessage = useCallback(async (content: string, topicOverride?: string) => {
    const sel = selectionRef.current;
    if (!sel || !content.trim()) return;
    try {
      if (sel.kind === "channel") {
        // An empty sel.topic means "new topic" mode (see ConversationList's
        // "+ New topic" row) — the composer supplies the topic name the user
        // just typed, and that becomes this thread's topic going forward.
        const topic = (topicOverride ?? sel.topic).trim();
        if (!topic) {
          message.warning("Enter a topic name first.");
          return;
        }
        const isNewTopic = !sel.topic;
        await sendStreamMessage(sel.streamName, topic, content);
        loadTopicsFor(sel.streamId);
        // Best-effort audit log — see logZulipTopicCreated's own docblock for
        // why this needs its own backend call (the send above talks to Zulip
        // directly, bypassing our backend entirely).
        if (isNewTopic) logZulipTopicCreated(sel.streamId, topic);
        const nextSel: Selection = { ...sel, topic };
        setSelection(nextSel);
        await loadThread(nextSel);
        return;
      }
      await sendDirectMessage(sel.userIds, content);
      refreshDmConversations();
      await loadThread(sel);
    } catch (e: any) {
      message.error(e?.message || "Failed to send message.");
    }
  }, [loadThread, loadTopicsFor, refreshDmConversations]);

  // Fetches the message's raw markdown source and stages the quote block for
  // the open thread's Composer to insert (see Composer's pendingComposerText
  // effect). consumePendingComposerText resets it back to null once picked
  // up, so the same quote can't be re-inserted on an unrelated later render.
  const quoteMessage = useCallback(async (msg: ZulipMessage) => {
    try {
      const raw = await getRawMessageContent(msg.id);
      setPendingComposerText(buildQuoteBlock(msg, raw));
    } catch (e: any) {
      message.error(e?.message || "Failed to quote message.");
    }
  }, []);

  const consumePendingComposerText = useCallback(() => setPendingComposerText(null), []);

  // "Forward message" — same quote block as quoteMessage, sent straight to a
  // different destination instead of staged in the current thread's
  // Composer (mirrors Zulip's own forward flow, which is quote-and-reply
  // aimed at a destination the user picks instead of the one they're in).
  const forwardMessage = useCallback(async (msg: ZulipMessage, dest: ForwardDestination) => {
    try {
      const raw = await getRawMessageContent(msg.id);
      const content = buildQuoteBlock(msg, raw);
      if (dest.kind === "channel") {
        const topic = dest.topic.trim();
        if (!topic) {
          message.warning("Enter a topic name first.");
          return;
        }
        await sendStreamMessage(dest.stream.name, topic, content);
        loadTopicsFor(dest.stream.stream_id);
        selectChannel(dest.stream, topic);
      } else {
        await sendDirectMessage(dest.userIds, content);
        refreshDmConversations();
        selectDm(dest.userIds, dest.label);
      }
      setForwardTarget(null);
      message.success("Message forwarded.");
    } catch (e: any) {
      message.error(e?.message || "Failed to forward message.");
    }
  }, [loadTopicsFor, selectChannel, refreshDmConversations, selectDm]);

  // ---- live updates, fed by useZulipEvents' long-poll ----
  // Handles every "message" event the queue delivers, regardless of which
  // thread (if any) is currently open: appends it live if it belongs to the
  // active thread (marking it read immediately), otherwise bumps the right
  // unread bucket; DMs also upsert the conversation list so a brand-new
  // conversation or an updated preview shows up without a separate fetch.
  const handleIncomingMessage = useCallback((msg: ZulipMessage) => {
    const isMine = myUserId !== null && msg.sender_id === myUserId;
    const appendIfNew = () =>
      setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg].sort((a, b) => a.id - b.id)));

    if (msg.type === "private") {
      if (myUserId === null) return;
      const conversation = dmConversationFromMessage(msg, myUserId);
      if (!conversation) return;
      setDmConversations((prev) => upsertDmConversation(prev, conversation));

      const sel = selectionRef.current;
      const isActive = sel?.kind === "dm" && dmKeyFromUserIds(sel.userIds) === conversation.key;
      if (isActive) {
        appendIfNew();
        if (!isMine) markMessagesRead([msg.id]).catch(() => {});
      } else if (!isMine) {
        bumpUnread("byDmKey", conversation.key, 1);
        if (isMention(msg)) bumpMentions(1);
      }
      return;
    }

    // Stream (channel) message.
    if (msg.stream_id === undefined) return;
    const sel = selectionRef.current;
    const isActive = sel?.kind === "channel" && sel.streamId === msg.stream_id && sel.topic === msg.subject;
    if (isActive) {
      appendIfNew();
      if (!isMine) markMessagesRead([msg.id]).catch(() => {});
    } else if (!isMine) {
      bumpUnread("byChannelTopic", `${msg.stream_id}:${msg.subject ?? ""}`, 1);
      if (isMention(msg)) bumpMentions(1);
    }
    // A brand-new topic on a channel the user already has expanded (topics
    // loaded) should show up in that list without re-expanding it.
    if (streamTopicsRef.current[msg.stream_id]) {
      loadTopicsFor(msg.stream_id);
    }
  }, [myUserId, bumpUnread, bumpMentions, loadTopicsFor]);

  messageHandlerRef.current = handleIncomingMessage;

  // Clears any stale "is typing" state left over from the previous thread.
  useEffect(() => {
    Object.values(typingTimeoutsRef.current).forEach(clearTimeout);
    typingTimeoutsRef.current = {};
    setTypingSenderIds(new Set());
  }, [selection]);

  const handleTypingEvent = useCallback((ev: ZulipEvent) => {
    const senderId = ev.sender?.user_id;
    if (senderId === undefined || senderId === myUserId) return; // ignore our own echo
    const sel = selectionRef.current;
    if (!sel) return;

    let matches = false;
    if (ev.message_type === "direct" && sel.kind === "dm") {
      // The conversation's full "other participants" set, from this event's
      // point of view, is the sender plus whichever recipients aren't us.
      const otherIds = [senderId, ...(ev.recipients ?? []).map((r) => r.user_id).filter((id) => id !== myUserId && id !== senderId)];
      matches = dmKeyFromUserIds(otherIds) === dmKeyFromUserIds(sel.userIds);
    } else if (ev.message_type === "stream" && sel.kind === "channel") {
      matches = ev.stream_id === sel.streamId && ev.topic === sel.topic;
    }
    if (!matches) return;

    clearTimeout(typingTimeoutsRef.current[senderId]);
    if (ev.op === "start") {
      setTypingSenderIds((prev) => new Set(prev).add(senderId));
      // Safety net: auto-clear if a "stop" event is ever missed (dropped
      // connection, tab backgrounded, etc.) rather than showing it forever.
      typingTimeoutsRef.current[senderId] = setTimeout(() => {
        setTypingSenderIds((prev) => {
          const next = new Set(prev);
          next.delete(senderId);
          return next;
        });
      }, 15000);
    } else if (ev.op === "stop") {
      setTypingSenderIds((prev) => {
        const next = new Set(prev);
        next.delete(senderId);
        return next;
      });
    }
  }, [myUserId]);

  typingHandlerRef.current = handleTypingEvent;

  const typingUsers = useMemo(
    () =>
      [...typingSenderIds].map((id) => realmUsers.find((u) => u.user_id === id)?.full_name ?? "Someone"),
    [typingSenderIds, realmUsers],
  );

  const sendTypingSignal = useCallback((op: "start" | "stop") => {
    const sel = selectionRef.current;
    if (!sel) return;
    const target = sel.kind === "dm" ? { kind: "dm" as const, userIds: sel.userIds } : { kind: "channel" as const, streamId: sel.streamId, topic: sel.topic };
    if (target.kind === "channel" && !target.topic) return; // no topic chosen yet in "new topic" mode
    sendTypingStatus(op, target).catch(() => {});
  }, []);

  const createChannel = useCallback(async (opts: { name: string; description?: string; inviteOnly: boolean; principals: number[] }) => {
    await createOrJoinChannel(opts);
    // Zulip's subscribe response doesn't include the new stream_id (see chatApi.ts) —
    // re-fetch and hand the resolved stream straight back so the caller (the
    // "created" flow in NewChannelModal) doesn't have to rely on its own,
    // possibly-stale `subscriptions` closure to find it.
    const subs = await getMySubscriptions();
    setSubscriptions(subs);
    return subs.find((s) => s.name === opts.name) ?? null;
  }, []);

  const invite = useCallback(async (streamName: string, principals: number[]) => {
    await inviteToChannel(streamName, principals);
  }, []);

  // Optimistic: flips pin_to_top in local state immediately so
  // groupAndSortChannels re-buckets the row into/out of "Pinned Channels"
  // without waiting on a round trip, then reverts + surfaces the error if
  // the call actually fails (e.g. Zulip's "not subscribed" — shouldn't
  // happen here since this is only ever called for a row already in
  // `subscriptions`, but the realtime queue is still the source of truth).
  const togglePinned = useCallback(async (stream: ZulipStream) => {
    const nextPinned = !stream.pin_to_top;
    setSubscriptions((prev) => prev.map((s) => (s.stream_id === stream.stream_id ? { ...s, pin_to_top: nextPinned } : s)));
    try {
      await setChannelPinned(stream.stream_id, nextPinned);
    } catch (e: any) {
      setSubscriptions((prev) => prev.map((s) => (s.stream_id === stream.stream_id ? { ...s, pin_to_top: !nextPinned } : s)));
      message.error(e?.message || "Failed to update pin.");
    }
  }, []);

  const unreadForChannelTopic = useCallback((streamId: number, topic: string) => unread.byChannelTopic[`${streamId}:${topic}`] ?? 0, [unread]);
  const unreadForDm = useCallback((userIds: number[]) => unread.byDmKey[dmKeyFromUserIds(userIds)] ?? 0, [unread]);
  // Aggregate across every topic in the channel — drives the channel row's
  // own unread indicator (previously only expanded topic rows showed one).
  const unreadForStream = useCallback((streamId: number) => {
    const prefix = `${streamId}:`;
    let total = 0;
    for (const [key, count] of Object.entries(unread.byChannelTopic)) {
      if (key.startsWith(prefix)) total += count;
    }
    return total;
  }, [unread]);

  const value = useMemo<ChatContextValue>(() => ({
    ready,
    myUserId,
    mode,
    setMode,
    open,
    toggleOpen,
    setOpen,
    subscriptions,
    browseStreams,
    channelFolders,
    streamTopics,
    loadTopicsFor,
    refreshSubscriptions,
    refreshBrowseStreams,
    expandedStreamIds,
    toggleStreamExpanded,
    dmConversations,
    refreshDmConversations,
    selection,
    selectChannel,
    selectDm,
    clearSelection,
    activeView,
    setActiveView,
    unread,
    enableReadReceipts,
    readReceipts,
    loadReadReceipts,
    messages,
    messagesLoading,
    hasOlder,
    loadOlderMessages,
    sendMessage,
    toggleReaction,
    pendingComposerText,
    consumePendingComposerText,
    quoteMessage,
    forwardTarget,
    setForwardTarget,
    forwardMessage,
    typingUsers,
    typingIntervals,
    sendTypingSignal,
    unreadTotal: unread.count,
    unreadForChannelTopic,
    unreadForDm,
    unreadForStream,
    realmUsers,
    refreshRealmUsers,
    presenceStatusFor,
    roleFor,
    userTypeFor,
    createChannel,
    invite,
    togglePinned,
    newChannelOpen,
    setNewChannelOpen,
    inviteTarget,
    setInviteTarget,
    newDmOpen,
    setNewDmOpen,
    setCanManageChatChannels,
  }), [
    ready, myUserId, mode, setMode, open, toggleOpen,
    subscriptions, browseStreams, channelFolders, streamTopics, loadTopicsFor, refreshSubscriptions, refreshBrowseStreams,
    expandedStreamIds, toggleStreamExpanded,
    dmConversations, refreshDmConversations,
    selection, selectChannel, selectDm, clearSelection,
    activeView, setActiveView,
    enableReadReceipts, readReceipts, loadReadReceipts,
    messages, messagesLoading, hasOlder, loadOlderMessages, sendMessage, toggleReaction,
    pendingComposerText, consumePendingComposerText, quoteMessage, forwardTarget, setForwardTarget, forwardMessage,
    typingUsers, typingIntervals, sendTypingSignal,
    unread, unreadForChannelTopic, unreadForDm, unreadForStream,
    realmUsers, refreshRealmUsers, presenceStatusFor, roleFor, userTypeFor,
    createChannel, invite, togglePinned,
    newChannelOpen, inviteTarget, newDmOpen,
    setCanManageChatChannels,
  ]);

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};
